import { useEffect, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Button, Card, Spinner, Alert, AlertDescription, Badge,
  Table, TableHeader, TableBody, TableHead, TableRow, TableCell,
  EmptyState,
} from '@agion/ui'
import { Blocks, RefreshCw } from 'lucide-react'
import { PageHeader } from '@/layouts/InfraPageHeader'
import { formatDate } from '@/core/infra_utils'
import { listTerraformModules, flattenModuleVersions } from './infra_terraform.service'
import type { TerraformModuleVersion } from './infra_terraform.service'

const moduleVariant: Record<string, 'default' | 'info' | 'success' | 'warning' | 'brand'> = {
  'gcp-vm': 'info',
  'gcp-gke': 'brand',
  'k3s-cluster': 'success',
}

function formatSize(bytes: number): string {
  if (bytes === 0) return '—'
  const mb = bytes / (1024 * 1024)
  return mb < 1 ? `${(bytes / 1024).toFixed(0)} KB` : `${mb.toFixed(1)} MB`
}

export function TerraformModulesPage() {
  const { t } = useTranslation(['pages'])
  const [versions, setVersions] = useState<TerraformModuleVersion[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const loadModules = useCallback(async () => {
    try {
      setIsLoading(true)
      setError(null)
      const modules = await listTerraformModules()
      setVersions(flattenModuleVersions(modules))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load terraform modules')
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    void loadModules()
  }, [loadModules])

  return (
    <div className="p-6 space-y-6">
      <PageHeader
        title={t('pages:terraformModules.title', 'Terraform Modules')}
        description={t('pages:terraformModules.description', 'Container-packaged Terraform modules for infrastructure provisioning')}
        icon={Blocks}
        actions={
          <Button variant="outline" size="sm" onClick={() => void loadModules()}>
            <RefreshCw size={14} className="mr-1" />
            {t('pages:terraformModules.refresh', 'Refresh')}
          </Button>
        }
      />

      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Spinner />
        </div>
      ) : error ? (
        <Alert variant="error">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : versions.length === 0 ? (
        <Card>
          <EmptyState
            icon={Blocks}
            title={t('pages:terraformModules.empty.title', 'No terraform modules')}
            description={t('pages:terraformModules.empty.description', 'Terraform module containers will appear here after they are built and pushed to the registry.')}
          />
        </Card>
      ) : (
        <Table variant="modern">
          <TableHeader variant="modern">
            <TableRow variant="modern">
              <TableHead variant="modern">{t('pages:terraformModules.columns.module', 'Module')}</TableHead>
              <TableHead variant="modern">{t('pages:terraformModules.columns.version', 'Version')}</TableHead>
              <TableHead variant="modern">{t('pages:terraformModules.columns.digest', 'Digest')}</TableHead>
              <TableHead variant="modern">{t('pages:terraformModules.columns.size', 'Size')}</TableHead>
              <TableHead variant="modern">{t('pages:terraformModules.columns.pushed', 'Pushed')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody variant="modern">
            {versions.map((v, i) => (
              <TableRow key={`${v.module}-${v.version}-${i}`} variant="modern">
                <TableCell>
                  <Badge variant={moduleVariant[v.module] ?? 'default'}>
                    {v.module}
                  </Badge>
                </TableCell>
                <TableCell className="font-mono text-sm font-medium">{v.version}</TableCell>
                <TableCell className="font-mono text-xs text-slate-400">
                  {v.digest ? `${v.digest.substring(0, 19)}...` : '—'}
                </TableCell>
                <TableCell className="text-slate-500">{formatSize(v.sizeBytes)}</TableCell>
                <TableCell className="text-slate-500">{v.pushedAt ? formatDate(v.pushedAt) : '—'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  )
}
